// Handle Buy It Now button clicks to show "Checking Availability" message
if (!customElements.get('buy-it-now-handler')) {
  customElements.define(
    'buy-it-now-handler',
    class BuyItNowHandler extends HTMLElement {
      constructor() {
        super();
        this.clickPending = false;
        this.cleanupOnPageLoad();
        this.setupBuyItNowHandlers();
        this.handleDisabledState();
      }

      handleDisabledState() {
        // Check if initially disabled (no variant selected)
        if (this.dataset.disabled === 'true') {
          this.disablePaymentButtons();
        }

        // Listen for URL changes (variant selection)
        this.setupVariantChangeListener();
      }

      setupVariantChangeListener() {
        // Listen for variant changes via URL
        const checkVariantInUrl = () => {
          const urlParams = new URLSearchParams(window.location.search);
          const hasVariant = urlParams.has('variant');
          
          if (hasVariant) {
            this.enablePaymentButtons();
          } else {
            this.disablePaymentButtons();
          }
        };

        // Check on popstate (browser back/forward)
        window.addEventListener('popstate', checkVariantInUrl);

        // Also listen for custom variant change events
        document.addEventListener('variant:changed', checkVariantInUrl);

        // Subscribe to pub/sub variant changes if available
        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          subscribe(PUB_SUB_EVENTS.variantChange, () => {
            // Small delay to ensure URL is updated
            setTimeout(checkVariantInUrl, 50);
          });
        }

        // MutationObserver to watch for URL changes in data attributes
        const productForm = this.closest('product-form');
        if (productForm) {
          const observer = new MutationObserver(() => {
            checkVariantInUrl();
          });
          const variantInput = productForm.querySelector('input[name="id"]');
          if (variantInput) {
            observer.observe(variantInput, { attributes: true, attributeFilter: ['value'] });
          }
        }
      }

      disablePaymentButtons() {
        this.dataset.disabled = 'true';
        const paymentButtons = this.querySelectorAll('.shopify-payment-button__button');
        paymentButtons.forEach((button) => {
          button.setAttribute('disabled', 'disabled');
          button.style.opacity = '0.5';
          button.style.cursor = 'not-allowed';
          button.style.pointerEvents = 'none';
        });

        // Also disable the payment button container
        const container = this.querySelector('.shopify-payment-button');
        if (container) {
          container.style.opacity = '0.5';
          container.style.pointerEvents = 'none';
        }
      }

      enablePaymentButtons() {
        this.dataset.disabled = 'false';
        const paymentButtons = this.querySelectorAll('.shopify-payment-button__button');
        paymentButtons.forEach((button) => {
          button.removeAttribute('disabled');
          button.style.opacity = '';
          button.style.cursor = '';
          button.style.pointerEvents = '';
        });

        // Also enable the payment button container
        const container = this.querySelector('.shopify-payment-button');
        if (container) {
          container.style.opacity = '';
          container.style.pointerEvents = '';
        }
      }

      cleanupOnPageLoad() {
        // Clean up any leftover transition elements when page loads (e.g., from browser back button)
        const existingTransitions = document.querySelectorAll('.premium-checkout-transition');
        existingTransitions.forEach(el => el.remove());
        document.body.classList.remove('checkout-transitioning');
        
        // Clear the checkout flag if we're back on the page
        sessionStorage.removeItem('isGoingToCheckout');
      }

      setupBuyItNowHandlers() {
        // Wait for Shopify's payment buttons to be rendered
        const checkForButtons = setInterval(() => {
          const paymentButtons = this.querySelectorAll('.shopify-payment-button__button');
          
          if (paymentButtons.length > 0) {
            clearInterval(checkForButtons);
            this.attachHandlers(paymentButtons);
          }
        }, 100);

        // Clear interval after 5 seconds if buttons don't appear
        setTimeout(() => clearInterval(checkForButtons), 5000);
      }

      attachHandlers(buttons) {
        // Apply disabled state if no variant is selected
        if (this.dataset.disabled === 'true') {
          this.disablePaymentButtons();
        }
        buttons.forEach((button) => {
          // Create overlay for "Checking Availability" message
          const overlay = this.createOverlay();
          const container = button.closest('.shopify-payment-button') || button.parentElement;
          
          // Ensure container has relative positioning
          if (container) {
            container.style.position = 'relative';
            container.appendChild(overlay);
          }

          button.addEventListener('click', (e) => {
            // If disabled due to no variant selected, block the click
            if (this.dataset.disabled === 'true') {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              return;
            }
            // If already processing, allow the click through
            if (this.clickPending) {
              return;
            }

            // Prevent immediate submission
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();

            // Show overlay with message and keep it visible
            overlay.style.display = 'flex';
            
            // Prevent any visual changes to the button
            button.style.pointerEvents = 'none';

            // After 1 second, change text and proceed with the action
            setTimeout(() => {
              // Change text to "Reserved for you" with special animation
              const messageSpan = overlay.querySelector('span');
              if (messageSpan) {
                messageSpan.textContent = window.buyItNowStrings?.reserved_for_you || 'Reserved for you';
                messageSpan.classList.add('reserved');
              }
              
              // Trigger premium page transition after a brief moment
              setTimeout(() => {
                this.showPremiumTransition();
                
                // Mark that we're allowing the next click
                this.clickPending = true;
                
                // Trigger the actual button click after transition starts
                setTimeout(() => {
                  button.click();
                }, 300);
              }, 400);
            }, 1000);
          }, { capture: true });
        });
      }

      createOverlay() {
        const overlay = document.createElement('div');
        overlay.classList.add('buy-it-now-overlay');
        
        const message = document.createElement('span');
        message.textContent = window.buyItNowStrings?.checking_availability || 'Checking Availability';
        overlay.appendChild(message);
        
        return overlay;
      }

      showPremiumTransition() {
        // Set flag to indicate we're intentionally going to checkout
        sessionStorage.setItem('isGoingToCheckout', 'true');
        
        // Add body class for page zoom effect
        document.body.classList.add('checkout-transitioning');
        
        // Create premium transition overlay
        const transitionOverlay = document.createElement('div');
        transitionOverlay.className = 'premium-checkout-transition';
        
        // Create content container
        const content = document.createElement('div');
        content.className = 'premium-checkout-transition-content';
        
        // Create icon with circle
        const icon = document.createElement('div');
        icon.className = 'premium-checkout-transition-icon';
        
        const circle = document.createElement('div');
        circle.className = 'premium-checkout-transition-circle';
        icon.appendChild(circle);
        
        // Create main text
        const text = document.createElement('div');
        text.className = 'premium-checkout-transition-text';
        text.textContent = window.buyItNowStrings?.taking_to_checkout || 'Taking You to Checkout';
        
        // Create subtext
        const subtext = document.createElement('div');
        subtext.className = 'premium-checkout-transition-subtext';
        subtext.textContent = window.buyItNowStrings?.preparing_experience || 'Preparing your premium experience...';
        
        // Assemble everything
        content.appendChild(icon);
        content.appendChild(text);
        content.appendChild(subtext);
        transitionOverlay.appendChild(content);
        document.body.appendChild(transitionOverlay);
        
        // Activate transition with a slight delay for smooth effect
        setTimeout(() => {
          transitionOverlay.classList.add('active');
        }, 50);
      }
    }
  );
}

// Global cleanup handler for browser back-forward cache (bfcache)
// This is essential for mobile browsers (iOS Safari, Chrome mobile) which restore pages from cache
window.addEventListener('pageshow', function(event) {
  // If the page was restored from bfcache (back/forward button)
  if (event.persisted) {
    // Clean up any leftover transition elements
    const existingTransitions = document.querySelectorAll('.premium-checkout-transition');
    existingTransitions.forEach(el => el.remove());
    document.body.classList.remove('checkout-transitioning');
    
    // Clear the checkout flag
    sessionStorage.removeItem('isGoingToCheckout');
    
    // Also clean up any overlays that might be stuck
    const overlays = document.querySelectorAll('.buy-it-now-overlay');
    overlays.forEach(overlay => {
      overlay.style.display = 'none';
    });
    
    // Reset any buttons that might be disabled
    const buttons = document.querySelectorAll('.shopify-payment-button__button');
    buttons.forEach(button => {
      button.style.pointerEvents = '';
    });
  }
});

